import { fill } from '@/copy';
import { shellCopy } from '@/copy/shell';
import { Button } from './kit';

/**
 * What the update prompt draws. Kept apart from UpdatePrompt (which talks to the service worker
 * through a virtual module) so it can be rendered in a test.
 */
export interface UpdateViewProps {
  /** 426 from a call, or a server minClient above this build: nothing else on screen is usable. */
  forced: boolean;
  /** A new worker is installed and waiting. */
  needRefresh: boolean;
  /** This phone keeps the book of a live game: the optional toast stays away. */
  held: boolean;
  waitingPlays: number;
  working: boolean;
  /** The new worker is still being fetched; the button says so instead of "Subbing in". */
  downloading?: boolean;
  /** The download did not finish in time. Nothing was reloaded, so the old build is still running. */
  stalled?: boolean;
  stuck: boolean;
  onApply(): void;
  onLater(): void;
}

export function UpdateView({
  forced,
  needRefresh,
  held,
  waitingPlays,
  working,
  downloading = false,
  stalled = false,
  stuck,
  onApply,
  onLater,
}: UpdateViewProps) {
  const workingLabel = downloading ? shellCopy.update.downloading : shellCopy.update.working;
  if (forced) {
    return (
      <div
        className="fixed inset-0 z-(--z-modal) grid place-items-center bg-lilac p-6"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="tts-update-title"
      >
        <div className="tts-card flex max-w-sm flex-col gap-4 p-5 text-center">
          <p id="tts-update-title" className="tts-display text-2xl">
            {shellCopy.update.requiredTitle}
          </p>
          <p className="font-medium">{shellCopy.update.required}</p>
          {waitingPlays > 0 && (
            <p className="tts-card-pale px-3 py-2 text-sm font-semibold" data-testid="update-plays">
              {waitingPlays === 1 ? shellCopy.update.playsOne : fill(shellCopy.update.plays, { n: waitingPlays })}
            </p>
          )}
          <Button variant="primary" size="lg" onClick={onApply} disabled={working}>
            {working ? workingLabel : shellCopy.update.requiredAction}
          </Button>
          {(stuck || stalled) && (
            <p role="alert" className="text-sm font-semibold">
              {stalled ? shellCopy.update.stalled : shellCopy.update.stuck}
            </p>
          )}
        </div>
      </div>
    );
  }

  if (!needRefresh || held) return null;
  return (
    <div className="tts-pt-safe pointer-events-none fixed inset-x-0 top-0 z-(--z-toast) flex justify-center px-4 pt-3">
      <div
        role="status"
        className="tts-card pointer-events-auto mt-[var(--tts-header-h)] flex w-full max-w-md items-center gap-3 bg-butter px-4 py-3 animate-tts-drop"
      >
        <p className="flex-1 font-bold">{shellCopy.update.title}</p>
        <Button variant="go" onClick={onApply} disabled={working}>
          {working ? workingLabel : shellCopy.update.action}
        </Button>
        <Button variant="ghost" onClick={onLater} aria-label={shellCopy.update.later}>
          {shellCopy.update.later}
        </Button>
      </div>
    </div>
  );
}
